<template>
  <div v-if="isOpen" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
    <div class="flex items-center justify-center min-h-screen pt-20 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Overlay -->
      <div class="fixed inset-0 bg-gray-700 bg-opacity-75 transition-opacity" @click="closeWithConfirmation"></div>

      <!-- Modal panel -->
      <div class="inline-block align-bottom bg-transparent rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:mt-20">
        <!-- Paso 1: Registro -->
        <SignUpModal 
          v-if="currentStep === 'signup'" 
          :pendingContractData="pendingContractData"
          @close="closeWithConfirmation"
          @signup-success="handleSignupSuccess"
        />
        
        <!-- Paso 2: Completar Perfil -->
        <CompleteProfileModal 
          v-if="currentStep === 'completeProfile'" 
          :pendingContractData="pendingContractData"
          @close="closeWithConfirmation"
          @profile-complete="handleProfileComplete"
        />
        
        <!-- Diálogo de confirmación para cerrar -->
        <div v-if="showConfirmClose" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg shadow-xl p-6 max-w-sm mx-auto">
            <h3 class="text-lg font-medium text-gray-900 mb-4">{{ t('onboardingModal.confirmCloseTitle') }}</h3>
            <p class="text-sm text-gray-500 mb-4">{{ t('onboardingModal.confirmCloseMessage') }}</p>
            <div class="flex justify-end space-x-4">
              <button 
                @click="showConfirmClose = false" 
                class="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-md shadow-sm focus:outline-none"
              >
                {{ t('onboardingModal.continueButton') }}
              </button>
              <button 
                @click="confirmClose" 
                class="px-4 py-2 text-sm text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none"
              >
                {{ t('onboardingModal.exitButton') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import SignUpModal from './SignUpModal.vue';
import CompleteProfileModal from './CompleteProfileModal.vue';

const { t } = useI18n();

// Props y emits
const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false
  },
  initialStep: {
    type: String,
    default: 'signup'
  }
});

const emit = defineEmits(['close', 'complete']);

// Estado del modal
const currentStep = ref(props.initialStep);
const pendingContractData = ref({});
const showConfirmClose = ref(false);
const router = useRouter();

// Manejar evento window para recibir los datos del contrato
onMounted(() => {
  // Recuperar datos del localStorage
  const storedData = localStorage.getItem('pendingContractData');
  if (storedData) {
    pendingContractData.value = JSON.parse(storedData);
  }
});

// Limpiar al cerrar
onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleEscKey);
});

// Evento de teclado para cerrar modal con ESC
const handleEscKey = (e) => {
  if (e.key === 'Escape') {
    closeWithConfirmation();
  }
};

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    window.addEventListener('keydown', handleEscKey);
  } else {
    window.removeEventListener('keydown', handleEscKey);
  }
});

// Cerrar con confirmación
const closeWithConfirmation = () => {
  showConfirmClose.value = true;
};

// Confirmar cierre
const confirmClose = () => {
  showConfirmClose.value = false;
  emit('close');
};

// Manejar éxito del registro
const handleSignupSuccess = (userData) => {
  currentStep.value = 'completeProfile';
};

// Manejar éxito de completar perfil
const handleProfileComplete = (userData) => {
  // Verify pending data before using it
  const currentPendingData = pendingContractData.value;

  // Try to read directly from localStorage if ref failed
  const storedDataString = localStorage.getItem('pendingContractData');
  let finalContractData = currentPendingData; // Use ref by default

  if (!finalContractData || Object.keys(finalContractData).length === 0) {
    if (storedDataString) {
      try {
        finalContractData = JSON.parse(storedDataString);
      } catch (e) {
        console.error('Error parsing localStorage data:', e);
        finalContractData = { type: 'rent', location: '', price: '' }; // Fallback
      }
    }
  }

  // Limpiar datos temporales
  localStorage.removeItem('pendingContractData');
  
  // Asegurarse de que los valores sean los correctos y con los nombres adecuados
  const queryParams = {
    contractType: finalContractData.type || 'rent',
    propertyLocation: finalContractData.location || '',
    propertyPrice: finalContractData.price ? finalContractData.price.toString() : ''
  };
  
  // Use location.href to avoid parameter loss issues
  const urlParams = new URLSearchParams();
  for (const [key, value] of Object.entries(queryParams)) {
    // Include parameter always, even if empty
    urlParams.append(key, value || '');
  }
  
  // Concatenate parameters to route
  const url = `/contracts/add?${urlParams.toString()}`;
  
  // Use location.href for direct redirect that preserves parameters
  window.location.href = url;
  
  // Emit completion event
  emit('complete', { 
    userData, 
    contractData: finalContractData // Use verified data
  });
  
  // Cerrar modal
  emit('close');
};
</script>

<style scoped>
/* Asegurar que el modal tenga suficiente espacio en la parte superior */
.sm\:mt-20 {
  margin-top: 5rem !important;
}
</style> 